/**
 * Inspired by https://github.com/SocialGouv/matomo-next v1.4.0
 * See initial commit for original file content
 */

import { default as Router } from 'next/router';

const isExcludedUrl = (url: string, patterns: RegExp[]): boolean => {
  let excluded = false;
  patterns.forEach((pattern) => {
    if (pattern.exec(url) !== null) {
      excluded = true;
    }
  });
  return excluded;
};

interface InitSettings {
  url: string;
  tagManagerUrl?: string;
  siteId: string;
  jsTrackerFile?: string;
  phpTrackerFile?: string;
  excludeUrlsPatterns?: RegExp[];
  manualPageViewTracking?: boolean;
}

interface Dimensions {
  dimension1?: string;
  dimension2?: string;
  dimension3?: string;
  dimension4?: string;
  dimension5?: string;
  dimension6?: string;
  dimension7?: string;
  dimension8?: string;
  dimension9?: string;
  dimension10?: string;
}

function embedMatomoScript(src: string) {
  const scriptElement = document.createElement('script');
  const refElement = document.getElementsByTagName('script')[0];
  scriptElement.type = 'text/javascript';
  scriptElement.async = true;
  scriptElement.defer = true;
  scriptElement.src = src;
  if (refElement.parentNode) {
    refElement.parentNode.insertBefore(scriptElement, refElement);
  }
}

// Push custom events
export function push(
  args: (Dimensions | number[] | string[] | number | string | null | undefined)[]
): void {
  if (!window['_paq']) {
    window['_paq'] = [];
  }
  (window['_paq'] as any[]).push(args);
}

// Initialize the tracker
export function init({
  url,
  tagManagerUrl,
  siteId,
  jsTrackerFile = 'matomo.js',
  phpTrackerFile = 'matomo.php',
  excludeUrlsPatterns = [],
  manualPageViewTracking = false,
}: InitSettings): void {
  window['_paq'] = window['_paq'] !== null ? window['_paq'] : [];
  if (!url) {
    throw new Error('Matomo disabled, missing url');
  }
  const excludedUrl =
    typeof window !== 'undefined' && isExcludedUrl(window.location.pathname, excludeUrlsPatterns);

  //use matomo cookieless tracking
  push(['disableCookies']);

  // initial load uses location.pathname
  // subsequent pageviews use  Router.pathname
  if (!excludedUrl && !manualPageViewTracking) {
    push(['trackPageView']);
  }

  push(['enableLinkTracking']);
  push(['setTrackerUrl', `${url}/${phpTrackerFile}`]);
  push(['setSiteId', siteId]);

  embedMatomoScript(`${url}/${jsTrackerFile}`);
  if (tagManagerUrl) {
    window['_mtm'] = window['_mtm'] || [];
    (window['_mtm'] as any[]).push({ 'mtm.startTime': new Date().getTime(), event: 'mtm.Start' });
    embedMatomoScript(tagManagerUrl);
  }

  Router.events.on('routeChangeComplete', (path: string): void => {
    if (isExcludedUrl(path, excludeUrlsPatterns)) {
      return;
    }

    // setTimeout to ensure that the page title had been updated
    const timeout = setTimeout(() => {
      push(['setCustomUrl', path]); 
      push(['setDocumentTitle', document.title]);
      push(['trackPageView']);
      clearTimeout(timeout);
    }, 10);
  });
}

export default init;
